import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "../components/AdminSidebar";
import AOS from "aos";
import "aos/dist/aos.css";
import "bootstrap/dist/css/bootstrap.min.css";

// ✅ Function to auto-generate slug from category name
const slugify = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[\s\W-]+/g, "-")
    .replace(/^-+|-+$/g, "");

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "" });
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [discountInput, setDiscountInput] = useState({}); // { [catId]: value }

  const navigate = useNavigate();

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/categories`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setCategories(res.data);
    } catch (err) {
      if (err.response?.status === 403) navigate("/admin/login");
    }
  };

  useEffect(() => {
    AOS.init();
    fetchCategories();
  }, []);

  // Update discount for a category
  const handleSetDiscount = async (catId) => {
    const token = localStorage.getItem("token");
    const discountPercent = Number(discountInput[catId]) || 0;
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/categories/${catId}`,
        { discountPercent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCategories();
      setDiscountInput({ ...discountInput, [catId]: "" });
    } catch (err) {
      alert("Failed to update discount");
    }
  };

  // Remove discount (set to 0)
  const handleRemoveDiscount = async (catId) => {
    const token = localStorage.getItem("token");
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/categories/${catId}`,
        { discountPercent: 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCategories();
      setDiscountInput({ ...discountInput, [catId]: "" });
    } catch (err) {
      alert("Failed to remove discount");
    }
  };

  // ✅ Handle name input & auto-generate slug
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {
      setForm({
        ...form,
        name: value,
        slug: slugify(value),
      });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      if (editingId) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/categories/${editingId}`,
          form,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        // Only send name for new category
        await axios.post(
          `${process.env.REACT_APP_API_URL}/categories`,
          { name: form.name },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
      setForm({ name: "", slug: "" });
      setEditingId(null);
      fetchCategories();
    } catch (err) {
      alert("Error saving category" + (err.response?.data?.error || err.message));
    }
  };

  const handleEdit = (cat) => {
    setForm(cat);
    setEditingId(cat.id);
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem("token");
    if (window.confirm("Delete this category?")) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/categories/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        fetchCategories();
      } catch (err) {
        alert("Delete failed");
      }
    }
  };

  const handleViewProducts = (catId) => {
    navigate(`/admin/products?category=${catId}`);
  };

  const filteredCategories = categories.filter((cat) =>
    cat.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminSidebar>
      <div className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="h3 fw-bold">Manage Categories</h1>
          <div className="form-inline">
            <input
              type="text"
              className="form-control"
              placeholder="Search categories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mb-4">
          <div className="input-group">
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Category Name"
              className="form-control"
              required
            />
            <button type="submit" className="btn btn-success">
              {editingId ? "Update" : "Add"} Category
            </button>
          </div>
        </form>

        <div className="row">
          {filteredCategories.map((cat, idx) => (
            <div
              key={cat.id}
              className="col-md-6 mb-4"
              data-aos="fade-up"
              data-aos-delay={`${idx * 50}`}
            >
              <div className="card shadow border-0">
                <div className="card-body d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="card-title mb-2">{cat.name}</h5>
                    <p className="mb-1"><strong>ID:</strong> {cat.id}</p>
                    <p className="mb-1"><strong>Slug:</strong> {cat.slug}</p>
                    <p className="mb-1"><strong>Total Products:</strong> {cat.productCount ?? 0}</p>
                    <p className="mb-1">
                      <strong>Discount Offer:</strong>{" "}
                      {cat.discountPercent > 0
                        ? <span style={{ color: "red" }}>{cat.discountPercent}% OFF</span>
                        : <span style={{ color: "#888" }}>No offer</span>
                      }
                    </p>
                    {/* --- Discount Controls --- */}
                    <div className="d-flex align-items-center mb-2">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        className="form-control form-control-sm me-2"
                        placeholder="Set discount %"
                        value={discountInput[cat.id] ?? ""}
                        onChange={e =>
                          setDiscountInput({ ...discountInput, [cat.id]: e.target.value })
                        }
                        style={{ width: "100px" }}
                      />
                      <button
                        className="btn btn-sm btn-outline-success me-2"
                        onClick={() => handleSetDiscount(cat.id)}
                      >
                        Set Offer
                      </button>
                      {cat.discountPercent > 0 && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemoveDiscount(cat.id)}
                        >
                          Remove Offer
                        </button>
                      )}
                    </div>
                    {/* --- End Discount Controls --- */}
                  </div>
                  <div className="text-end">
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => handleEdit(cat)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger me-2"
                      onClick={() => handleDelete(cat.id)}
                    >
                      Delete
                    </button>
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => handleViewProducts(cat.id)}
                    >
                      View Products
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminSidebar>
  );
};

export default CategoriesPage;